/**
 * The two contracts a customer signs: repair, and replacement.
 *
 * This is the legal text, so it lives in code rather than in a table: it is
 * reviewed in a pull request, it is versioned by git, and the tests in
 * `test/contract.test.ts` pin the sentences that must never quietly change.
 * What a customer actually signed is not read from here at signing time — the
 * presented document snapshots the rendered contract, so amending this file
 * never edits an agreement somebody has already agreed to (ADR 0032).
 *
 * ## Where the words come from
 *
 * Both are transcriptions of the Google Docs the owner maintains,
 * "CopperRidge - Repair Contract" and "CopperRidge - Replacement Contract",
 * as they stood on 2026-08-16. Jonathon, that day: "drive is more current and
 * is what needs to be used." That replaced the single retail-cash contract this
 * file used to hold, and the differences are listed in
 * `docs/adr/0037-two-contracts-chosen-by-what-was-sold.md`.
 *
 * Three deliberate departures from the Drive wording, and only three:
 *
 *  1. **The company is named by merge field, never in the text.** The Docs say
 *     "CopperRidge Roofing and Exteriors, LLC" throughout, along with a street
 *     address, an office number and a cancellation mailbox. All of them come
 *     from `company_settings` instead. A contract that hard-codes any of them
 *     is wrong the next time one changes, and the trading name has changed
 *     once already (ADR 0022).
 *  2. **The licence number is not printed.** Both Docs carry "Alabama
 *     Residential Roofer License No. 31013". Left off on the owner's
 *     instruction of 2026-08-16 ("let's just leave it off").
 *  3. **The insurance paragraphs are conditional.** See `insurance` below.
 *
 * ## Insurance
 *
 * The replacement contract in Drive ends with a long block that only applies to
 * a customer filing a claim — supplements, direction to pay, overhead and
 * profit, the fee for taking an approved claim to another contractor, and the
 * deductible notice Alabama law requires. A retail cash customer is not filing
 * a claim and should not be reading any of it.
 *
 * So those paragraphs render only when `insurance` is set, which is only ever
 * on an insurance bid. Jonathon, 2026-08-16: "just let insurance verbiage be on
 * insurance bid types only." Two sentences buried mid-paragraph elsewhere in
 * the document are scoped the same way, for the same reason —
 * `INSURANCE_ONLY_SENTENCES` keeps them where a test can see them.
 *
 * ## Initials
 *
 * There are none. Neither Doc asks for any, and the tap-to-initial boxes this
 * file used to draw went six → two → zero over three weeks on the owner's
 * instruction. `CONTRACT_INITIAL_KEYS` is kept, empty, because the customer's
 * Edge Function keeps a transcription of it that a test binds — and because a
 * document sent before today is frozen with its boxes still on it and must
 * still require them.
 */

import { isCommercialJob } from "./autoExpenses.js";

/** Bump when the wording changes. Stamped onto every document at send. */
export const CONTRACT_VERSION = "2026-09-28-agreement";

/** The commercial agreement's own version. See `contractVersionFor`. */
export const COMMERCIAL_CONTRACT_VERSION = "2026-09-28-commercial-agreement";

/**
 * The agreement for a deck alone, which has no Warranty section (ADR 0281).
 * Its own label for the same reason the commercial one has one: the wording
 * differs, so a signed record has to say which agreement it was.
 */
export const DECK_CONTRACT_VERSION = "2026-10-14-deck-agreement";

/** The commercial agreement for a deck alone: commercial wording, no Warranty. */
export const COMMERCIAL_DECK_CONTRACT_VERSION =
  "2026-10-14-commercial-deck-agreement";

/**
 * Which agreement this job is signing.
 *
 * A version rather than a bump, because the residential text did not change:
 * an agreement signed on a house tomorrow is word for word the one signed
 * yesterday and it would be a lie to stamp it with a new date. Only the
 * commercial job is reading something new, so only it is labelled as such —
 * and a signed record then says exactly which of the two it was.
 *
 * `trades` are the same trades the document hands `contractKindFor` and
 * `warrantySectionFor`. When they drop the Warranty section — a replacement
 * agreement for a deck alone — the deck version is stamped instead. A repair
 * agreement never drops its section, so it keeps its usual label. Left out,
 * the trades say nothing and the standard label stands.
 */
export function contractVersionFor(
  jobType: string | null | undefined,
  trades: readonly (string | null | undefined)[] = [],
  longerWorkmanship: readonly LongerWorkmanship[] = [],
): string {
  const deckOnly =
    !warrantySectionFor(trades) &&
    contractKindFor({ bidType: null, trades }) === "replacement";
  const base = isCommercialJob(jobType)
    ? deckOnly
      ? COMMERCIAL_DECK_CONTRACT_VERSION
      : COMMERCIAL_CONTRACT_VERSION
    : deckOnly
      ? DECK_CONTRACT_VERSION
      : CONTRACT_VERSION;
  return longerWorkmanship.length > 0 ? base + LONGER_WORKMANSHIP_LABEL : base;
}

/**
 * Added to the version of an agreement that names a workmanship warranty
 * longer than five years, whose wording differs in three sentences
 * (`withLongerWorkmanship`). Every other agreement keeps its label, because
 * its words did not change.
 */
export const LONGER_WORKMANSHIP_LABEL = "+longer-workmanship";
/*
 * Bumped from "2026-08-16-drive" because the words changed, not the terms:
 * every "contract" the customer reads is now "agreement" (Jonathon,
 * 2026-08-17: "I prefer the word agreement over contract"). Nothing anybody
 * has signed moves — a presented document carries the version it was built
 * with (ADR 0032) — but a customer comparing their copy to a fresh one should
 * be able to see that the two are not the same wording.
 *
 * "Contractor" is untouched: it names a party, not the paper.
 *
 * Bumped again on 2026-08-24, from "2026-08-17-agreement": the Job Details
 * block came off both contracts along with the four boxes that fed it
 * (ADR 0056). That is a paragraph fewer on the page, which is a wording change
 * by any reading of the line above.
 *
 * Bumped again on 2026-09-28, with the commercial version beside it, because
 * both agreements share the full-replacement paragraph: "replace all pipe
 * boots; re-flash as needed" became "replace all pipe boots and roof vents
 * with new; inspect all flashing and replace it as needed" (Jonathon,
 * 2026-09-28: "the flashing will be replaced as needed but the accessories
 * (pipebots, vents) will be replaced").
 */

/** The courts named in the venue clause. Pelham is in Shelby, not Jefferson. */
export const CONTRACT_VENUE_COUNTY = "Shelby County";

/** How long the customer has to cancel, by Alabama law. */
export const RESCISSION_BUSINESS_DAYS = 3;

/**
 * Which contract this is.
 *
 * Chosen by what was sold, not by what the lead was called — see
 * `contractKindFor`.
 */
export type ContractKind = "repair" | "replacement";

/**
 * Who signs for the company when nobody else can.
 *
 * Jonathon, 2026-08-16: "a typed name in a cursive font is fine. you can use
 * Lysa Sandoval on all of them." It was every contract until 2026-09-07, when
 * he asked for the rep who sold the job to sign their own: "the company
 * signature can now be whoever the sales rep is. doesn't have to be lysa ... if
 * there's not a rep it can be lysa and her phone number."
 *
 * So this is the fallback rather than the answer — a job with no rep on it, and
 * the office standing behind the document instead. It is frozen into each
 * document at send, so changing it here never restyles a contract somebody has
 * already signed.
 */
export const COMPANY_SIGNER_NAME = "Lysa Sandoval";

export type ContractBlock =
  | { kind: "heading"; text: string }
  | { kind: "paragraph"; text: string }
  /**
   * A place the customer taps to initial.
   *
   * Nothing emits one any more. The kind stays because frozen documents from
   * before 2026-08-16 still carry them, and the renderer and the server both
   * still have to meet those.
   */
  | { kind: "initial"; key: string; text: string };

/** The values a contract is filled in with. Missing ones are left visible. */
export interface ContractValues {
  "company.name"?: string | null | undefined;
  "company.address"?: string | null | undefined;
  "company.email"?: string | null | undefined;
  /**
   * Where a customer writes to cancel inside the three business days.
   *
   * Its own field rather than `company.email`, because that one is also the
   * {{company.email}} token in every outbound template. A cancellation notice
   * has to reach somebody who is watching for one, and the owner's Docs use a
   * different mailbox for it. Falls back to `company.email` when unset.
   */
  "company.cancellation_email"?: string | null | undefined;
  "company.phone"?: string | null | undefined;
  "customer.name"?: string | null | undefined;
  "job.address"?: string | null | undefined;
}

export interface ContractOptions {
  kind: ContractKind;
  /**
   * Render the insurance-claim paragraphs. Insurance bids only, and never on a
   * repair contract — the repair Doc has no insurance section at all.
   */
  insurance?: boolean;
  /**
   * The job's type — "residential" or "commercial".
   *
   * Five paragraphs are written to somebody who lives in the building. On a
   * commercial job they are replaced with the versions in
   * `COMMERCIAL_PARAGRAPHS`; everything else in the agreement, including every
   * term and every number, is the same document.
   */
  jobType?: string | null | undefined;
  /**
   * Render the Warranty section. Off only when every trade sold is a deck
   * (`warrantySectionFor`); a deck carries no warranty (ADR 0281).
   */
  warranty?: boolean;
  /**
   * The roofs on the proposal whose workmanship warranty runs past five
   * years: a GAF new construction roof sold with its materials (ADR 0318).
   * Empty or left out, the agreement reads as it always has.
   */
  longerWorkmanship?: readonly LongerWorkmanship[];
}

/** A roof whose workmanship warranty runs past the agreement's five years. */
export interface LongerWorkmanship {
  /** The shingle, as the agreement names it: "GAF Timberline HDZ". */
  roof: string;
  years: number;
}

/**
 * There are no initial boxes left. Kept, and kept exported, because the
 * customer's Edge Function transcribes this list and
 * supabase/test/contract-initials-match.test.ts binds the two together.
 */
export const CONTRACT_INITIAL_KEYS = [] as const;

export type ContractInitialKey = (typeof CONTRACT_INITIAL_KEYS)[number];

/**
 * The sentences that must never appear on a contract that is not an insurance
 * bid, kept here so a test can assert their absence.
 *
 * The first two are the ones that hide inside otherwise-general paragraphs and
 * would survive dropping the insurance section wholesale. The third is the
 * heading of the section itself.
 */
export const INSURANCE_ONLY_SENTENCES: readonly string[] = [
  "In the event that your insurance company denies a filed claim, a pre-agreement will be canceled with proof of denial.",
  "However, these repairs are not a part of the insurance claim unless otherwise noted",
  "The Following Paragraphs Pertain Only to Customers Filing an Insurance Claim",
];

/**
 * The Alabama deductible notice, as the replacement Doc words it.
 *
 * It sits inside the insurance block and nowhere else. The statute is about
 * paying a deductible on a property insurance claim; a cash customer has no
 * claim and no deductible, and printing it at them says nothing true. The
 * previous contract carried it on every document, which was flagged at the time
 * as a question for the owner rather than a decision; scoping it here answers
 * that question.
 */
export const DEDUCTIBLE_NOTICE =
  "Alabama law requires a person insured under a property insurance policy to " +
  "pay any deductible applicable to a claim made under the policy. It is a " +
  "violation of Alabama law for a seller of goods or services who reasonably " +
  "expects to be paid wholly or partly from the proceeds of a property " +
  "insurance claim to knowingly allow the insured person to fail to pay, or " +
  "assist the insured person's failure to pay, the applicable insurance " +
  "deductible.";

/**
 * The right of rescission on the replacement contract, verbatim.
 *
 * The three business days run from the date the customer signs. Jonathon,
 * 2026-08-16: "3 day cancellation starts on the day the customer signs." That
 * is the date they put next to their signature, which is what the Docs' own
 * "date of the agreement" means on paper.
 */
export const RIGHT_OF_RESCISSION =
  "You have the right to cancel this agreement within 3 (three) business days " +
  "of the date you sign it under Alabama State Law. By signing below, the " +
  "Customer confirms they have been informed of the cancellation information " +
  'found on this agreement titled "Notice of Cancellation for Agreement".';

/** The repair contract's shorter rescission clause, verbatim. */
export const REPAIR_RIGHT_OF_RESCISSION =
  "Assuming work has not commenced, the Customer has the right to cancel this " +
  "agreement within three (3) business days of the date they sign it by " +
  "emailing notice of cancellation to {{company.cancellation_email}}. " +
  "Cancellations after " +
  "this period may incur a restocking fee up to 25% of the total contracted " +
  "amount.";

/*
 * ===========================================================================
 * The five paragraphs that assume the Customer lives there
 * ===========================================================================
 *
 * Jonathon, 2026-08-28, having read the presentation change: "do the contract
 * too."
 *
 * The proposal is handled by a phrase substitution (`commercialWording.ts`),
 * and the agreement deliberately is not. Two reasons, and both are about what
 * kind of document this is.
 *
 * The proposal's words are *data*: the Tools screen and the Present screen
 * rewrite them, so a second stored copy would drift and a substitution over
 * whatever is there today is the only thing that cannot. The agreement's words
 * are *code* — they live in this file, they change only when somebody edits
 * this file, and `CONTRACT_VERSION` records when. Nothing can drift, so the
 * argument for a substitution is gone.
 *
 * And a substitution inside a liability release is a bad idea on its own
 * terms. "ensure the safety of their family members, friends, children and
 * pets" is a list of the people the Customer undertakes to look after. Swapping
 * a word inside it by regular expression changes who is covered, in a document
 * somebody signs, without anybody having read the result. These are written
 * out instead, so what a commercial customer signs is a sentence a person
 * wrote and a person can read.
 *
 * Where the commercial sentence widens the Customer's undertaking it does so
 * on purpose — "anyone they permit on the premises" covers more than a list of
 * four categories, not fewer, because a narrower duty on a bigger site is the
 * wrong direction to guess in.
 *
 * The residential text is untouched, byte for byte. `contractVersionFor()`
 * says so: a residential agreement keeps the 2026-08-17 version it has always
 * had, and only a commercial one is stamped with the new one.
 */

/** Repair contract, Liability Disclosure. */
const REPAIR_LIABILITY_DISCLOSURE =
  "The Customer acknowledges that the Property will be a construction " +
  "site. The Company is not liable for injuries or damages resulting from " +
  "falling debris, nails, or unsecured items; damage to driveways caused " +
  "by vehicles used during the repair process; or damage to utility lines " +
  "(gas, water, sprinkler, etc.) during repairs. It is the Customer's " +
  "responsibility to use caution while on-site, secure fragile or loose " +
  "items on the Property, and assume risks associated with utility lines.";

/** The same, where the vehicles park on a lot and nobody lives upstairs. */
const COMMERCIAL_REPAIR_LIABILITY_DISCLOSURE =
  "The Customer acknowledges that the Property will be a construction " +
  "site. The Company is not liable for injuries or damages resulting from " +
  "falling debris, nails, or unsecured items; damage to driveways, parking " +
  "areas or other paved surfaces caused by vehicles used during the repair " +
  "process; or damage to utility lines (gas, water, sprinkler, etc.) during " +
  "repairs. It is the Customer's responsibility to use caution while " +
  "on-site, secure fragile or loose items on the Property, and assume risks " +
  "associated with utility lines.";

/** Replacement contract, Liability Disclosure Addendum. */
const LIABILITY_ADDENDUM =
  "The Customer's property will be a construction site; therefore, the " +
  "Customer must use caution when entering and exiting the Property and " +
  "ensure the safety of their family members, friends, children and pets " +
  "on the premises. The Customer understands and accepts the risks of " +
  "falling debris and errant nails. It is the Customer's responsibility to " +
  "use reasonable caution and the Customer agrees to release and hold " +
  "harmless the Company of any responsibility for any injury, damage to " +
  "Property, or death that may occur due in part or in whole to any " +
  "negligence on Customer's part. The Customer understands it is their " +
  "responsibility to secure any items on the Customer's Property that may " +
  "be fragile or might fall resulting in injury or death. Any damage to " +
  "any items is the sole responsibility of the Customer.";

/**
 * The same undertaking, owed to the people who are actually on a commercial
 * site: employees, tenants, visitors, other trades. Deliberately wider than
 * the residential list rather than a swap of one word for another.
 */
const COMMERCIAL_LIABILITY_ADDENDUM =
  "The Customer's property will be a construction site; therefore, the " +
  "Customer must use caution when entering and exiting the Property and " +
  "ensure the safety of anyone they permit on the premises, including " +
  "their employees, tenants, visitors, contractors and pets. The Customer " +
  "understands and accepts the risks of falling debris and errant nails. It " +
  "is the Customer's responsibility to use reasonable caution and the " +
  "Customer agrees to release and hold harmless the Company of any " +
  "responsibility for any injury, damage to Property, or death that may " +
  "occur due in part or in whole to any negligence on Customer's part. The " +
  "Customer understands it is their responsibility to secure any items on " +
  "the Customer's Property that may be fragile or might fall resulting in " +
  "injury or death. Any damage to any items is the sole responsibility of " +
  "the Customer.";

/** Replacement contract, the paragraph about where the trucks stand. */
const VEHICLES_ON_THE_DRIVEWAY =
  "All vehicles operated by employees of the Company are rated for " +
  "driveway usage, and any damage or cracks resulting from routine " +
  "driveway usage or parking in the driveway to complete the job is not " +
  "the responsibility of {{company.name}}.";

/** A commercial building has a lot, a dock or a service road, not a drive. */
const COMMERCIAL_VEHICLES_ON_THE_LOT =
  "All vehicles operated by employees of the Company are rated for " +
  "driveway and parking-lot usage, and any damage or cracks resulting from " +
  "routine driveway, parking-area or service-road usage, or from parking to " +
  "complete the job, is not the responsibility of {{company.name}}.";

/** Replacement contract, General Terms — the carbon monoxide paragraph. */
const CARBON_MONOXIDE =
  "Furnace, water heater, and appliance flue vents may run through the " +
  "roof deck and can be disturbed during roofing work. The Customer " +
  "understands it is the Customer's responsibility to ensure these " +
  "connections are secure, or to request that a Company Representative " +
  "inspect these crucial connections, so that Carbon Monoxide does not " +
  "enter the Customer's dwelling. It is the Customer's responsibility to " +
  "ensure the safety of the Customer's family, and the Customer agrees to " +
  "hold harmless the Company of all liability associated with Carbon " +
  "Monoxide and furnace vents becoming disconnected. The Customer further " +
  "understands that Carbon Monoxide is a deadly gas and that serious " +
  "injury or death may occur as a result of furnace vents becoming " +
  "disconnected.";

/**
 * The same warning for a building rather than a dwelling.
 *
 * The hazard is identical and the sentence is not softened anywhere: a
 * commercial roof carries rooftop units and flue vents exactly as a house
 * does, and the people at risk are occupants rather than a family.
 */
const COMMERCIAL_CARBON_MONOXIDE =
  "Furnace, water heater, and appliance flue vents may run through the " +
  "roof deck and can be disturbed during roofing work. The Customer " +
  "understands it is the Customer's responsibility to ensure these " +
  "connections are secure, or to request that a Company Representative " +
  "inspect these crucial connections, so that Carbon Monoxide does not " +
  "enter the Customer's building. It is the Customer's responsibility to " +
  "ensure the safety of the building's occupants, and the Customer agrees " +
  "to hold harmless the Company of all liability associated with Carbon " +
  "Monoxide and furnace vents becoming disconnected. The Customer further " +
  "understands that Carbon Monoxide is a deadly gas and that serious " +
  "injury or death may occur as a result of furnace vents becoming " +
  "disconnected.";

/** The insurance walkthrough, step two. */
const ADJUSTER_STEP_TWO =
  "2. Call your insurance company to confirm they have received the final " +
  "invoice from the Company. If they claim not to have received the " +
  "invoice, send them a copy of your invoice. Make sure to get the contact " +
  "information for the adjuster who is processing your claim. This will " +
  "most likely be a different adjuster than the one who inspected your " +
  "house originally.";

/** The adjuster inspected a property, which is true of both. */
const COMMERCIAL_ADJUSTER_STEP_TWO =
  "2. Call your insurance company to confirm they have received the final " +
  "invoice from the Company. If they claim not to have received the " +
  "invoice, send them a copy of your invoice. Make sure to get the contact " +
  "information for the adjuster who is processing your claim. This will " +
  "most likely be a different adjuster than the one who inspected your " +
  "property originally.";

/**
 * Which paragraph a commercial agreement prints instead.
 *
 * Keyed by the residential paragraph itself rather than by a name, so the two
 * cannot come apart: edit the residential text and the key stops matching, the
 * commercial variant stops being substituted, and the test that renders both
 * agreements fails. There is no third place to remember to update.
 */
export const COMMERCIAL_PARAGRAPHS = new Map<string, string>([
  [REPAIR_LIABILITY_DISCLOSURE, COMMERCIAL_REPAIR_LIABILITY_DISCLOSURE],
  [LIABILITY_ADDENDUM, COMMERCIAL_LIABILITY_ADDENDUM],
  [VEHICLES_ON_THE_DRIVEWAY, COMMERCIAL_VEHICLES_ON_THE_LOT],
  [CARBON_MONOXIDE, COMMERCIAL_CARBON_MONOXIDE],
  [ADJUSTER_STEP_TWO, COMMERCIAL_ADJUSTER_STEP_TWO],
]);

/**
 * Contractor's Default. On both Docs, word for word, and on neither of the
 * contracts this file used to hold.
 */
const CONTRACTORS_DEFAULT =
  "Owner shall give Contractor at least seven (7) days written notice and the " +
  "opportunity to cure, or such additional time as is reasonably necessary to " +
  "cure the alleged breach, before declaring Contractor in default of this " +
  "Agreement. It is Owner's responsibility to notify Contractor in writing " +
  "within seven (7) days (unless otherwise provided for in the Agreement) of " +
  "the occurrence of any claim, defect, or deficiency arising out of work " +
  "performed, services supplied, or materials provided by Contractor under " +
  "this Agreement (“Occurrence”).";

/** Closes both contracts, immediately above the signatures. */
const ACKNOWLEDGMENT =
  "By signing below, the Customer acknowledges they have read, understood, " +
  "and agree to the terms of this agreement in its entirety.";

/* -------------------------------------------------------------------------- */
/* The repair contract                                                        */
/* -------------------------------------------------------------------------- */

const REPAIR_BLOCKS: readonly ContractBlock[] = [
  { kind: "heading", text: "{{company.name}} — Repair Agreement" },
  {
    kind: "paragraph",
    text:
      "This agreement between {{company.name}} (hereinafter “the " +
      "Company”) and {{customer.name}} (the “Customer”) " +
      "establishes the terms for repair work to be completed at " +
      "{{job.address}} (the “Property”). By signing this document, " +
      "the Customer confirms they have read and understood the terms outlined " +
      "herein.",
  },

  { kind: "heading", text: "Liability Disclosure" },
  {
    kind: "paragraph",
    text: REPAIR_LIABILITY_DISCLOSURE,
  },

  { kind: "heading", text: "Repair Terms" },
  {
    kind: "paragraph",
    text:
      "Repairs are based on a visible inspection. If additional issues are " +
      "discovered, a change order and updated quote will be required before " +
      "proceeding. New materials and paint may not match existing materials " +
      "due to weathering and availability. Repairs do not address underlying " +
      "structural issues unless explicitly agreed upon in writing.",
  },

  { kind: "heading", text: "Warranty" },
  {
    kind: "paragraph",
    text:
      "Unless otherwise stated in the scope of work, repairs do not include a " +
      "warranty. If the repaired area leaks within 30 days, the Company will " +
      "inspect the issue. Additional work will require a new quote.",
  },

  { kind: "heading", text: "Right of Rescission" },
  { kind: "paragraph", text: REPAIR_RIGHT_OF_RESCISSION },

  { kind: "heading", text: "Payment Terms" },
  {
    kind: "paragraph",
    text:
      "Initial payment may be due upon agreement signing and prior to work " +
      "starting. Final payment is due upon completion of repairs. Payments " +
      "not received within 30 days are subject to penalties. Accepted payment " +
      "methods include personal checks, money orders, cashier's checks, debit " +
      "and credit cards (fee may apply), and ACH. Returned checks will incur a " +
      "$50 fee.",
  },

  { kind: "heading", text: "Failure to Pay Penalties" },
  {
    kind: "paragraph",
    text:
      "A 10% penalty will be assessed on the total remaining balance. All " +
      "discounts are revoked at the Company's discretion. Accounts may be sent " +
      "to collections or legal action pursued under Alabama law.",
  },

  { kind: "heading", text: "General Terms" },
  {
    kind: "paragraph",
    text:
      "Rotten decking and sheathing repairs are not included in the scope of " +
      "work unless otherwise stated. If rotten sheathing is discovered during " +
      "tearoff, a change order and updated estimate may be required. Debris " +
      "removal is included. Customers are responsible for ensuring the " +
      "worksite is accessible. The Company reserves the right to use Property " +
      "photos for marketing purposes. The Company performs work in accordance " +
      "with Alabama building codes and maintains insurance.",
  },

  { kind: "heading", text: "Contractor's Default" },
  { kind: "paragraph", text: CONTRACTORS_DEFAULT },

  { kind: "heading", text: "Venue" },
  {
    kind: "paragraph",
    text:
      "Any disputes arising from this agreement will be handled in the courts " +
      `of ${CONTRACT_VENUE_COUNTY}, Alabama.`,
  },

  { kind: "heading", text: "Acknowledgment" },
  { kind: "paragraph", text: ACKNOWLEDGMENT },
];

/* -------------------------------------------------------------------------- */
/* The replacement contract                                                   */
/* -------------------------------------------------------------------------- */

/** Everything above the insurance block. */
const REPLACEMENT_BLOCKS: readonly ContractBlock[] = [
  { kind: "heading", text: "{{company.name}} — Agreement & Payment Terms" },
  {
    kind: "paragraph",
    text:
      "{{company.name}} (hereinafter “the Company”) sets forth the " +
      "agreement between the Company and {{customer.name}} (the " +
      "“Customer”) to establish the working terms for work to be " +
      "completed at {{job.address}} (hereinafter the “Property”). In " +
      "addition to the working terms, this agreement also establishes the " +
      "agreed-upon payment schedule between the Company and the Customer.",
  },
  {
    kind: "paragraph",
    text:
      "By signing this document, you agree that you have read and understood " +
      "the quote, agreement, terms, and conditions outlined herein in its " +
      "entirety.",
  },

  /* The "Job Details" block used to sit here: shingle type, colour, delivery
     instructions and existing damage, four merge fields typed into the
     estimate's contract preview. Jonathon took it out on 2026-08-24 — "we
     don't need this. please remove anything associated with it".

     Removing the four boxes and leaving the paragraphs would have been the
     worse half of the change: `fill()` leaves an unmatched token visible on
     purpose, so every contract from then on would have printed
     "{{job.materials}}" to a customer. The paragraphs go with the boxes. What
     is being sold is already on the packages above, and the colour is on the
     material order, which is where it is acted on. */

  { kind: "heading", text: "Liability Disclosure Addendum" },
  {
    kind: "paragraph",
    text: LIABILITY_ADDENDUM,
  },
  {
    kind: "paragraph",
    text: VEHICLES_ON_THE_DRIVEWAY,
  },
  {
    kind: "paragraph",
    text:
      "Any punctured lines are not the responsibility of {{company.name}} " +
      "during the installation process. The Company is not responsible for any " +
      "damage to utility lines, including but not limited to gas lines, " +
      "sprinkler lines, water lines, or freon lines. By engaging the services " +
      "of the Company, the Customer acknowledges and assumes the risk " +
      "associated with the utility lines on their property. The Customer " +
      "releases the Company, its employees, and contractors from any liability " +
      "related to utility damage during our work. This release includes both " +
      "public and private utility lines.",
  },

  { kind: "heading", text: "Right of Rescission and Property Disclosure" },
  { kind: "paragraph", text: RIGHT_OF_RESCISSION },

  { kind: "heading", text: "Notice of Cancellation for Agreement" },
  // The insurance sentence that used to close this paragraph is scoped to
  // insurance bids and appended by `cancellationNotice()` below.
  { kind: "paragraph", text: "{{cancellation}}" },

  { kind: "heading", text: "General Terms" },
  {
    kind: "paragraph",
    text: CARBON_MONOXIDE,
  },
  {
    kind: "paragraph",
    text:
      "In the event the Company encounters un-nailable surfaces such as rotten " +
      "decking or sheathing, the Company will repair and/or replace such " +
      "un-nailable surfaces at the expense of the Customer. Not replacing " +
      "un-nailable surfaces will void your manufacturer warranty as well as " +
      "your 5 Year Workmanship Warranty from the Company. It is at the " +
      "discretion of the Company to remove existing underlayment or felted " +
      "areas on the Customer's Property. New underlayment may be placed over " +
      "existing in some areas. A thorough inspection of existing decking and " +
      "sheathing is still conducted of any areas with an additional felt layer.",
  },
  // The insurance sentence that used to sit mid-sentence here is scoped to
  // insurance bids and spliced in by `framingRepairs()` below.
  { kind: "paragraph", text: "{{framing}}" },
  {
    kind: "paragraph",
    text:
      "Asphalt shingles may take up to 2-4 weeks to soften, fully bond, and " +
      "flatten in appearance. Regarding repairs: the color of new materials " +
      "and paint may not match old materials and paint due to weathering and " +
      "product availability.",
  },
  {
    kind: "paragraph",
    text:
      "The Customer grants the Company a non-exclusive, worldwide, " +
      "royalty-free, perpetual license to use, reproduce, and display " +
      "photographs of the Property, the Property address, and related images " +
      "in our marketing materials. The Customer agrees to indemnify and hold " +
      "the Company harmless from any claims, damages, or liabilities arising " +
      "from the use of the pictures in the Company's promotional efforts.",
  },
  {
    kind: "paragraph",
    text:
      "The Company performs work in accordance with State and Local Building " +
      "Codes, manufacturer's recommended guidelines, and construction industry " +
      "best practices. The Company is bonded as required and carries Worker's " +
      "Compensation and General Liability Insurance. The Company also pulls " +
      "permits when and where required and purchases business licenses for " +
      "each city where work is performed.",
  },

  { kind: "heading", text: "Quote Terms" },
  {
    kind: "paragraph",
    text: "This quote may be withdrawn by the Company if not accepted within 30 days.",
  },
  {
    kind: "paragraph",
    text:
      "Replacement of any un-nailable surface (i.e. rotten wood) will be " +
      "charged to the Customer at a minimum of $85 per sheet of plywood and a " +
      "minimum of $50 per 1x6x16. The first three (3) sheets of plywood or " +
      "first five (5) 1x6x16 planks are included in your price.",
  },
  {
    kind: "paragraph",
    text:
      "Repair quotes of any kind or trade do not include a five (5) year " +
      "workmanship warranty.",
  },
  {
    kind: "paragraph",
    text:
      "Company Representatives do not make verbal agreements, and any terms not " +
      "disclosed on an agreement are considered null and void. Therefore, any " +
      "matters of importance to you should be explicitly written herein or " +
      "within an approved change order.",
  },
  {
    kind: "paragraph",
    text:
      "This quote is based on our inspection of the visible and accessible " +
      "portions of the property and assumes that there are no hidden or " +
      "underlying problems. After the start of a repair, if additional work is " +
      "required, the Customer will be immediately notified, and a change order " +
      "and updated quote will be required before moving forward.",
  },
  {
    kind: "paragraph",
    text:
      "This quote does not include the cost of any necessary roof " +
      "reinforcement or structural repairs. Such cost, as well as the cost of " +
      "any soffit, fascia, siding and gutter repairs and replacements, are not " +
      "included in the pricing or scope of work unless explicitly specified in " +
      "writing herein or in an approved change order. Therefore, this quote is " +
      "only for the services outlined in the quote and does not include any " +
      "additional work not specified in the quote.",
  },
  {
    kind: "paragraph",
    text:
      "If the quote includes pricing for a full roof replacement, such " +
      "replacement includes tearing off one layer of existing roofing shingles " +
      "and underlayment or felt. Additional layers of shingle tear away and " +
      "underlayment or felt tear away is $0.30 per square foot, per layer. " +
      "Also, for full roof replacement quotes, the Company will install one " +
      "layer of new synthetic felt underlayment nailed to the sheathing or " +
      "deck using approved fasteners; replace all pipe boots and roof vents " +
      "with new; inspect all flashing and replace it as needed; install ice " +
      "and water shield moisture barrier in all valleys; " +
      "install painted drip edge per code; tear off and haul away debris with " +
      "an onsite dumpster; provide a thorough cleanup; and provide a five (5) " +
      "year workmanship warranty.",
  },
  {
    kind: "paragraph",
    text:
      "For gutter quotes, the Company is not responsible for replacing or " +
      "repairing the fascia and soffits or any other defect that is caused " +
      "during the removal of the old gutters. The cost of any repair or " +
      "replacement of fascia, soffits, or other defects is the responsibility " +
      "of the Customer and will require another quote or a change order. The " +
      "Customer acknowledges that gutters range in various shapes and sizes " +
      "and that changing the shape or size of the gutters from that which was " +
      "existing may cause unintended damage for which the Company will not be " +
      "responsible. Gutter quotes will include a thorough clean-up and debris " +
      "removal.",
  },
  {
    kind: "paragraph",
    text:
      "For siding quotes, the Company will either back flash or caulk siding " +
      "butt joints. If the Customer requests back flashing, there could be an " +
      "additional charge. The Company will not paint siding unless it is " +
      "explicitly stated in the quote. Often the term Hardie Board is used " +
      "when referring to fiber cement siding without respect to any specific " +
      "brand. If the Customer requires a specific brand of siding, the " +
      "Customer should notify the Company and the specific brand should be " +
      "included in writing in the quote. Siding quotes will include a thorough " +
      "clean-up and debris removal. The Company reserves the right to choose " +
      "the brand and quality of paint. If the Customer requests a specific " +
      "brand or upgrade, a change order and updated price may be required. " +
      "Most siding materials come pre-primed. The Company will apply an " +
      "additional coat of primer tinted to the color of the Customer's choice " +
      "and an additional coat of paint. Upon removal of the siding and " +
      "discovery of additional layers and un-nailable surfaces, a change order " +
      "and updated price will be required.",
  },
  {
    kind: "paragraph",
    text:
      "If any of the flashing around the windows and doors needs to be " +
      "replaced, a change order and updated price will be required.",
  },

  { kind: "heading", text: "For Financing Customers" },
  {
    kind: "paragraph",
    text:
      "If financing, the Customer is responsible for the dealer fee charged by " +
      "the selected third-party finance company, and such fee must be paid " +
      "directly to the contractor or dealer. The financing and payment terms " +
      "are strictly between the Customer and the selected third-party " +
      "financing company. Any breakdown provided is for reference only. The " +
      "Company is not a finance company and cannot guarantee you will be " +
      "approved for financing by the third-party finance company. Please " +
      "reference documents from the third-party finance company for their " +
      "applicable terms and conditions.",
  },

  { kind: "heading", text: "Venue" },
  {
    kind: "paragraph",
    text:
      "All suits arising out of or related to this agreement shall be filed in " +
      `the courts of ${CONTRACT_VENUE_COUNTY}, Alabama.`,
  },

  { kind: "heading", text: "Warranty" },
  {
    kind: "paragraph",
    text:
      "The Company includes a five (5) Year Workmanship Warranty on all newly " +
      "installed systems, which protects against poor workmanship. The Company " +
      "is not responsible for normal wear and tear. See complete warranty " +
      "information for details. The warranty begins upon payment in full of " +
      "the total agreement amount and approved supplements. The warranty will " +
      "be voided by an unpaid agreement. Furthermore, the Company does not " +
      "warranty repairs of any kind.",
  },
  {
    kind: "paragraph",
    text:
      "For repairs: due to the condition of the roof and the nature of " +
      "repairs, if the roof continues to leak in the area of the repair within " +
      "30 days, a member of the Company will come back and inspect the area " +
      "repaired. If additional work is required, an additional quote will be " +
      "sent to the Customer.",
  },

  { kind: "heading", text: "Payments" },
  {
    kind: "paragraph",
    text:
      "The first payment is due upon agreement signature and prior to work " +
      "starting. Failure to make the first payment may result in work " +
      "stoppage. {{company.name}} is not liable for damages that may occur to " +
      "property due to work stoppage for failure to make the initial agreement " +
      "payment. This includes but is not limited to flooding, water damage, " +
      "and theft of material. Final payment is due to the Company upon " +
      "completion of work for each trade (roofing, siding, gutters, etc.). " +
      "Final payments not received within 30 days of completion will be " +
      "considered failure to pay and will be subject to Failure to Pay " +
      "Penalties. See Failure to Pay Penalties for further details.",
  },
  {
    kind: "paragraph",
    text:
      "We accept personal checks, money orders, cashier's checks, debit cards, " +
      "credit cards and ACH. Make checks payable to {{company.name}}. There is " +
      "a 3% convenience fee for credit card transactions. Returned " +
      "checks will result in a returned check fee of $50 and/or potential hot " +
      "check charges filed with the appropriate authorities. If mailing a " +
      "check, please mail to {{company.address}}.",
  },

  { kind: "heading", text: "Failure to Pay Penalties" },
  {
    kind: "paragraph",
    text:
      "A 10% penalty will be assessed against the total remainder due, all " +
      "discounts will be revoked at the sole discretion of the Company, and " +
      "the account is subject to being sent to a third-party collections " +
      "agency. Failure to pay may also result in Theft of Service charges " +
      "being filed per Alabama law in addition to any necessary civil remedies.",
  },

  { kind: "heading", text: "Severability" },
  {
    kind: "paragraph",
    text:
      "In the event any provision of this Agreement is determined to be void " +
      "or unenforceable, such determination shall not affect the remainder of " +
      "this Agreement, which shall continue to be in force.",
  },

  { kind: "heading", text: "Contractor's Default" },
  { kind: "paragraph", text: CONTRACTORS_DEFAULT },
];

/** The insurance block, appended to a replacement contract on a claim. */
const INSURANCE_BLOCKS: readonly ContractBlock[] = [
  {
    kind: "heading",
    text: "The Following Paragraphs Pertain Only to Customers Filing an Insurance Claim",
  },
  {
    kind: "paragraph",
    text:
      "This agreement authorizes the Company to interact with the Insurance " +
      "Carrier on the Customer's behalf for the purpose of discussing the " +
      "technical aspects of the insurance claim. By signing below, the " +
      "Customer authorizes the Company to complete all work specified on the " +
      "Insurance Loss Assessment. The Customer acknowledges that the Insurance " +
      "Loss Assessment will determine the final price owed to the Company for " +
      "the project. Upon insurance company approval, the Company becomes the " +
      "contractor of choice to complete all repairs as stated on the agreed " +
      "upon Insurance Loss Assessment.",
  },
  {
    kind: "paragraph",
    text:
      "The Company works hard to help the Customer's Property get the full " +
      "amount of coverage the Customer deserves. In order for the Company to " +
      "do this, the claim is supplemented so that the insurance carrier is " +
      "requested to pay for items that are necessary (i.e. items required by " +
      "code) but were left off the insurance company's original scope of work. " +
      "Therefore, the quoted amount herein is the minimum price for which the " +
      "Company will be doing the work. This means the price could increase; on " +
      "a Replacement Cost Value (RCV) policy the Customer would still be " +
      "responsible for the insurance deductible plus any agreed-upon upgrades. " +
      "Any additional money released by insurance is owed by the Customer to " +
      "the Company and is to be released to the Company either by endorsing " +
      "the check over to the Company or by paying the Company with any " +
      "acceptable means of payment (personal check, money order, cashier's " +
      "check, debit card, credit card or ACH).",
  },
  {
    kind: "paragraph",
    text:
      "There is a significant amount of time and money spent by the Company in " +
      "the process of assisting our Customers through the insurance process. " +
      "In the unlikely event that the Company assists in getting the " +
      "Customer's claim approved and the Customer decides not to hire the " +
      "Company for any reason, the Customer is responsible for paying 10% of " +
      "the total Replacement Cost Value (RCV) amount, which is considered by " +
      "the Company a fee for work performed by the Company assisting the " +
      "Customer through the insurance process. Such amount is due and payable " +
      "within 7 days of the Customer notifying the Company of their decision " +
      "to hire another contractor, or the date any other contractor performed " +
      "the work contracted to be performed by the Company. This provision does " +
      "not apply to a cancellation made within the 3 business day Right of " +
      "Rescission described above.",
  },
  {
    kind: "paragraph",
    text:
      "In the event that insurance does not approve the Customer's claim, the " +
      "Customer has the option to cancel this agreement prior to any work " +
      "being performed, or the Customer can move forward with a cash or " +
      "financing arrangement.",
  },
  { kind: "paragraph", text: DEDUCTIBLE_NOTICE },

  {
    kind: "heading",
    text: "Recovering of Withheld Depreciation and Supplements",
  },
  {
    kind: "paragraph",
    text:
      "It is the Customer's responsibility to determine the type of insurance " +
      "policy they have. If the Customer has an Actual Cash Value (ACV) " +
      "policy, the Customer will be responsible for paying the difference " +
      "between the ACV amount to be paid by the Insurance Company and the " +
      "Company's scope of work, in addition to the deductible. If the Customer " +
      "has a Replacement Cost Value (RCV) policy, your insurance company has " +
      "depreciated items on your claim. In order to recover the depreciation, " +
      "a final invoice and certificate of completion are sent to your " +
      "insurance company. In the event that items were missing from your " +
      "original estimate, the Company has also applied for supplemental items " +
      "with your insurance company. These supplemental items, if any, are " +
      "included on the insurance invoice and in the final invoice total.",
  },
  {
    kind: "paragraph",
    text:
      "Below are the steps you will need to take to ensure your recoverable " +
      "depreciation and supplements are released from your Insurance Company:",
  },
  { kind: "paragraph", text: "1. Have your claim number available." },
  {
    kind: "paragraph",
    text: ADJUSTER_STEP_TWO,
  },
  {
    kind: "paragraph",
    text:
      "3. Confirm everything is complete with the handling adjuster, and ask " +
      "them to release the recoverable depreciation and any applied-for " +
      "supplements. If requested, some insurance companies can directly " +
      "deposit the funds into your bank account. Otherwise, the check will " +
      "take between 5-7 business days to receive. If they have any questions " +
      "regarding supplements or the final invoice, please direct them to " +
      "{{company.phone}}.",
  },
  {
    kind: "paragraph",
    text:
      "4. Follow up with the handling adjuster within three (3) business days " +
      "to confirm payment has been processed and mailed to you. You may have " +
      "to follow up several times to expedite the process. Insurance companies " +
      "can get very busy, and it helps to stay on top of them to ensure your " +
      "funds are released in a timely manner.",
  },
  {
    kind: "paragraph",
    text:
      "5. Once you have received the remaining funds, call your Company " +
      "Representative to pay any balances owed and to schedule any remaining " +
      "trades. You may pay by personal check, cashier's check, or credit or " +
      "debit card (3% processing fee for credit cards) made payable " +
      "to {{company.name}}.",
  },
  {
    kind: "paragraph",
    text:
      "For invoice questions, contact your Company Representative or call our " +
      "billing department at {{company.phone}}. For supplement questions, " +
      "contact our insurance department at {{company.phone}}.",
  },
  {
    kind: "paragraph",
    text:
      "All insurance-approved supplements and recoverable depreciation will be " +
      "due to the Company. The Customer agrees to fully cooperate with any " +
      "necessary paperwork needed to seek supplement and recoverable " +
      "depreciation approval, and the Customer acknowledges they have no claim " +
      "to any supplemental funds or recoverable depreciation. If the Customer " +
      "does not cooperate with these requests, the additional supplements and " +
      "recoverable depreciation will be due at the sole responsibility of the " +
      "Customer. Any supplements or recoverable depreciation that are denied " +
      "by the insurance provider will not be the responsibility of the " +
      "Customer.",
  },

  { kind: "heading", text: "Work Completed Per Insurance Scope" },
  {
    kind: "paragraph",
    text:
      "All work will be performed per the detailed line item scope listed on " +
      "insurance paperwork, except for the items listed in the “work not " +
      "doing” section. Chimney flashing, flue caps and chase covers will " +
      "be inspected for damage and replaced strictly on an as-needed basis. " +
      "“Work not doing” totals will directly correlate to line items " +
      "in the Scope of Work provided by the Insurance Company. According to " +
      "this agreement, this work will not be done by the Company. The Customer " +
      "may be required to complete this work by their Mortgage Provider or " +
      "Insurance Company. This work will be the sole responsibility of the " +
      "Customer and does not allow for any deductions from the contracted " +
      "amount. Any additional work will need to be in writing. No work will be " +
      "done to your property without a written agreement, and payment terms " +
      "will not be made based on verbal agreements.",
  },

  { kind: "heading", text: "Discounts" },
  {
    kind: "paragraph",
    text:
      "Any discounts offered to the Customer by the Company may be due to the " +
      "Customer's Insurance Company per your insurance policy agreement. The " +
      "Company is not responsible for any agreements the Customer has with " +
      "their Insurance Company and will not be responsible for paying any " +
      "amounts due to their Insurance Company as a result of their agreement. " +
      "If the Customer's payment terms are not honored, all discounts offered " +
      "to the Customer will be revoked at the sole discretion of the Company. " +
      "See Failure to Pay Penalties for further details.",
  },

  { kind: "heading", text: "Third Party Authorization and Direction to Pay" },
  {
    kind: "paragraph",
    text:
      "The Customer authorizes {{company.name}} the following types of " +
      "authorization regarding their claim: request inspections, discuss and " +
      "request supplements, and request claim payment status (recoverable " +
      "depreciation and supplements).",
  },
  {
    kind: "paragraph",
    text:
      "Furthermore, I/We authorize the Insurance Company and Mortgage Company " +
      "(if applicable) to work directly with the Company in connection with " +
      "all aspects of processing the claim, including disbursement of claim " +
      "funds. The Customer also authorizes the Insurance Company and Mortgage " +
      "Company to remit all claim disbursements directly to {{company.name}} " +
      "at {{company.address}}.",
  },
  {
    kind: "paragraph",
    text:
      "Note: account information such as payment status, loan type, etc., is " +
      "not included within this authorization. This authorization will become " +
      "effective as of the date signed and will terminate at the time the " +
      "claim has been fully completed and closed by the Insurance Company and " +
      "Mortgage Company.",
  },

  { kind: "heading", text: "Overhead & Profit" },
  {
    kind: "paragraph",
    text:
      "Understanding the time, effort, energy and supervision necessary for " +
      "the restoration of my project, I do not have the time nor the resources " +
      "to coordinate or manage this project to completion. Therefore, this " +
      "statement is to inform my insurance company that my contractor of " +
      "choice, {{company.name}}, is my general contractor, and they will be " +
      "managing and coordinating all subcontractors and projects required to " +
      "complete the repair of my property.",
  },
  {
    kind: "paragraph",
    text:
      "Therefore, overhead and profit should be included in my estimate for " +
      "the following reasons:",
  },
  { kind: "paragraph", text: "1. {{company.name}} is a general contractor." },
  {
    kind: "paragraph",
    text:
      "2. They will directly hire and coordinate all subcontractors required " +
      "to complete my project.",
  },
  {
    kind: "paragraph",
    text:
      "3. As a general contractor coordinating the trades and work, they have " +
      "overhead costs not connected to specific line items on the estimate. " +
      "{{company.name}} must include overhead and profit to provide their " +
      "contracting services and stay in business, as they have expenses and " +
      "overhead above and beyond the normal expenses of laborers who do the " +
      "work themselves.",
  },
  {
    kind: "paragraph",
    text:
      "Please speak directly with {{company.name}} regarding any questions. " +
      "You can reach us at {{company.phone}}.",
  },
];

/** Closes the replacement contract, after the insurance block if there is one. */
const REPLACEMENT_CLOSING: readonly ContractBlock[] = [
  { kind: "heading", text: "Acknowledgment" },
  { kind: "paragraph", text: ACKNOWLEDGMENT },
];

/**
 * The Notice of Cancellation, which gains a sentence on an insurance bid.
 *
 * On paper it is one paragraph ending "…a pre-contract will be canceled with
 * proof of denial." That sentence is about a claim, so a cash customer does not
 * get it, and splicing it in here keeps the paragraph reading as one paragraph
 * either way rather than breaking it in two.
 */
function cancellationNotice(insurance: boolean): string {
  const base =
    "If the Customer chooses to exercise the 3 Day Right of Rescission before " +
    "work has been completed, the Customer must email notice of cancellation " +
    "to {{company.cancellation_email}}, time stamped no later than 3 " +
    "business days after " +
    "the date the Customer signed this agreement. Upon receipt of timely " +
    "notice, this agreement is null and void and the Company will perform no " +
    "work.";
  const claim = insurance
    ? " In the event that your insurance company denies a filed claim, a " +
      "pre-agreement will be canceled with proof of denial."
    : "";
  const restocking =
    " Agreements canceled outside of this period may result in a restocking " +
    "fee not to exceed 25% of the total contracted amount.";
  return base + claim + restocking;
}

/** The framing-and-decking paragraph, which gains a clause on a claim. */
function framingRepairs(insurance: boolean): string {
  const base =
    "A new roof, gutter and/or siding system will not in and of itself remedy " +
    "existing issues to framing, decking, sheathing, fascia or soffit. If " +
    "agreed upon in writing in this document or a subsequent change order, " +
    "any of these types of repairs can be made at the request and expense of " +
    "the Customer prior to the installation of the work that the Company and " +
    "the Customer contracted to have performed. However, ";
  const claim = insurance
    ? "these repairs are not a part of the insurance claim unless otherwise " +
      "noted, and repairs to these items cannot be completed after the " +
      "installation of the installed systems."
    : "repairs to these items cannot be completed after the installation of " +
      "the installed systems.";
  return base + claim;
}

/* -------------------------------------------------------------------------- */
/* Choosing which contract                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Whether a trade sells a replacement rather than a repair.
 *
 * Trades are free text on documents and are never renamed, only added and
 * deactivated (rule 8), so this reads the name rather than matching a fixed
 * list. Today's seven reference values split cleanly:
 *
 *   replacement   Roofing Replacement, Siding Replacement, Gutters, Decks
 *   repair        Roofing Repairs, Siding Repairs, All Other Repairs
 *
 * Jonathon, 2026-08-16: "full gutters, siding, deck, etc. would be
 * replacement." A trade added later that says "Repairs" lands on the repair
 * side without anyone editing this; anything else — Windows, Fencing — is new
 * work and lands on the replacement side, which is the fuller contract and the
 * safer default to be wrong in.
 *
 * An empty trade is neither. It is not evidence of a repair, so it must not
 * quietly pull a roof replacement onto the shorter contract; `contractKindFor`
 * treats it as no opinion at all.
 */
export function isReplacementTrade(trade: string | null | undefined): boolean {
  const name = (trade ?? "").trim().toLowerCase();
  if (name === "") return false;
  return !name.includes("repair");
}

export interface ContractKindInput {
  /** The job's bid type: "repair", "retail" or "insurance". */
  bidType: string | null | undefined;
  /**
   * Every trade on every estimate the document offers — line items and the
   * estimates themselves. A packet quoting four options is one contract, so one
   * replacement anywhere in it makes the whole document a replacement.
   */
  trades: readonly (string | null | undefined)[];
}

/**
 * Which contract goes at the bottom of this document.
 *
 * **What was sold decides, not what the lead was called.** Jonathon,
 * 2026-08-16: "if anything in the estimate is a replacement it's a replacement
 * contract." A job logged as a repair that grew into a new roof gets the
 * replacement contract; a retail lead that ended up as a $600 patch gets the
 * repair one. Bid type is only the tie-breaker for an estimate whose lines
 * carry no trade at all, which is a half-built estimate rather than an ordinary
 * one.
 *
 * Bid type still decides the insurance paragraphs — see `contractBlocks`.
 */
export function contractKindFor(input: ContractKindInput): ContractKind {
  if (input.trades.some(isReplacementTrade)) return "replacement";

  // A trade that is set and is not a replacement is a repair, and it is the
  // estimate saying so. Only a document with nothing to go on falls through to
  // the bid type.
  const named = input.trades.some((trade) => (trade ?? "").trim().length > 0);
  if (named) return "repair";

  return (input.bidType ?? "").trim().toLowerCase() === "repair"
    ? "repair"
    : "replacement";
}

/** Whether a trade sells a deck. Read by name, like `isReplacementTrade`. */
export function isDeckTrade(trade: string | null | undefined): boolean {
  return /\bdecks?\b/.test((trade ?? "").trim().toLowerCase());
}

/**
 * Whether the agreement keeps its Warranty section.
 *
 * A deck carries no warranty, so an agreement for a deck alone has none.
 * Sold with a roof, siding or gutters, the section stays as written, and it
 * names no deck. Jonathon, 2026-10-13: "yes. warranty in contract shouldn't
 * mention the deck" (ADR 0281).
 */
export function warrantySectionFor(
  trades: readonly (string | null | undefined)[],
): boolean {
  const named = trades.filter((trade) => (trade ?? "").trim().length > 0);
  return named.length === 0 || !named.every(isDeckTrade);
}

/** Whether the insurance paragraphs belong on this document. */
export const showsInsuranceTerms = (
  bidType: string | null | undefined,
  kind: ContractKind,
): boolean =>
  kind === "replacement" &&
  (bidType ?? "").trim().toLowerCase() === "insurance";

/* -------------------------------------------------------------------------- */
/* Rendering                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Where a cancellation goes, with the general mailbox as the fallback.
 *
 * A company that uses one address for both simply leaves `cancellation_email`
 * unset, and the contract reads as it always did. The fallback is here rather
 * than at the call site so it holds for every caller, including a test.
 */
const cancellationMailbox = (values: ContractValues): string | null =>
  values["company.cancellation_email"]?.trim() ||
  values["company.email"]?.trim() ||
  null;

/** Fill a token in, or leave it visible so a gap is obvious rather than blank. */
function fill(text: string, values: ContractValues): string {
  const resolved: ContractValues = {
    ...values,
    "company.cancellation_email": cancellationMailbox(values),
  };
  return text.replace(/{{\s*([\w.]+)\s*}}/g, (whole, token: string) => {
    const value = Object.hasOwn(resolved, token)
      ? resolved[token as keyof ContractValues]
      : undefined;
    return value == null || value === "" ? whole : String(value);
  });
}

/** The blocks less one section: its heading and everything up to the next. */
function withoutSection(
  blocks: readonly ContractBlock[],
  heading: string,
): ContractBlock[] {
  let skipping = false;
  return blocks.filter((block) => {
    if (block.kind === "heading") skipping = block.text === heading;
    return !skipping;
  });
}

const YEARS_IN_WORDS: Readonly<Record<number, string>> = {
  10: "ten",
  15: "fifteen",
  20: "twenty",
  25: "twenty-five",
  30: "thirty",
};

/** "ten (10)", the way the agreement writes every number of years. */
const yearsWorded = (years: number): string =>
  YEARS_IN_WORDS[years] ? `${YEARS_IN_WORDS[years]} (${years})` : String(years);

/**
 * The agreement's three five-year sentences, read for a proposal that sells a
 * roof whose workmanship warranty runs longer (ADR 0318).
 *
 * Jonathon, 2026-10-28: "if it's a labor only warranty the workmanship is five
 * years. if we're choosing the gaf etc. template with materials then it's 10
 * years for hdz, 15 years for uhdz, 5 years for titan." One agreement covers
 * every roof the proposal offers, so the Warranty paragraph keeps five years
 * for everything else and names each longer roof beside it; the two sentences
 * that said "5 Year" in passing say "your Workmanship Warranty" and point to
 * it. With no longer roof the text is returned untouched.
 */
export function withLongerWorkmanship(
  text: string,
  longer: readonly LongerWorkmanship[],
): string {
  if (longer.length === 0) return text;
  const roofs = [...new Map(longer.map((term) => [term.roof, term])).values()]
    .sort((a, b) => a.years - b.years)
    .map((term) => `${yearsWorded(term.years)} years on a ${term.roof}`);
  const named =
    roofs.length === 1
      ? roofs[0]
      : `${roofs.slice(0, -1).join(", ")} and ${roofs[roofs.length - 1]}`;
  return text
    .replace(
      "Workmanship Warranty on all newly installed systems, which protects",
      `Workmanship Warranty on all newly installed systems, or ${named} ` +
        "new construction roof installed with its materials, which protects",
    )
    .replace(
      "your 5 Year Workmanship Warranty from the Company",
      "your Workmanship Warranty from the Company",
    )
    .replace(
      "and provide a five (5) year workmanship warranty.",
      "and provide the workmanship warranty set out under Warranty below.",
    );
}

/** The contract, filled in for one job. */
export function contractBlocks(
  values: ContractValues,
  options: ContractOptions,
): ContractBlock[] {
  const insurance =
    options.kind === "replacement" && options.insurance === true;

  const replacement: ContractBlock[] = [
    ...REPLACEMENT_BLOCKS,
    ...(insurance ? INSURANCE_BLOCKS : []),
    ...REPLACEMENT_CLOSING,
  ];
  const source: readonly ContractBlock[] =
    options.kind === "repair"
      ? REPAIR_BLOCKS
      : options.warranty === false
        ? withoutSection(replacement, "Warranty")
        : replacement;

  /*
   * Five paragraphs, swapped whole. Before `fill`, so the substitution is over
   * the text this file wrote and not over a customer's name that happened to
   * land in it.
   */
  const commercial = isCommercialJob(options.jobType);
  const longer =
    options.kind === "replacement" ? (options.longerWorkmanship ?? []) : [];
  const worded = (text: string): string =>
    withLongerWorkmanship(
      (commercial ? COMMERCIAL_PARAGRAPHS.get(text) : undefined) ?? text,
      longer,
    );

  return source.map((block) => {
    // The two paragraphs whose own wording depends on the insurance flag. They
    // are placeholders in the block list so the order of the document stays
    // readable in one place.
    if (block.text === "{{cancellation}}") {
      return {
        kind: "paragraph",
        text: fill(worded(cancellationNotice(insurance)), values),
      };
    }
    if (block.text === "{{framing}}") {
      return {
        kind: "paragraph",
        text: fill(worded(framingRepairs(insurance)), values),
      };
    }
    return { ...block, text: fill(worded(block.text), values) };
  });
}

/** The contract as plain text — for the snapshot, and for a person to read. */
export function contractText(
  values: ContractValues,
  options: ContractOptions,
): string {
  return contractBlocks(values, options)
    .map((block) =>
      block.kind === "heading" ? `\n${block.text}\n` : block.text,
    )
    .join("\n")
    .trim();
}

/**
 * Every token a set of values cannot fill. A gap in a contract is a defect.
 *
 * Asked of the widest contract there is — a replacement with the insurance
 * paragraphs — so the warning in the Present modal does not change as the rep
 * flips the bid type, and so a token used only inside the insurance block still
 * gets noticed.
 */
export function unfilledContractTokens(
  values: ContractValues,
  options: ContractOptions = { kind: "replacement", insurance: true },
): string[] {
  const found = new Set<string>();
  for (const match of contractText(values, options).matchAll(
    /{{\s*([\w.]+)\s*}}/g,
  )) {
    found.add(match[1]!);
  }
  return [...found];
}

/**
 * Has the customer consented, given a name, and initialled everywhere the
 * document in front of them asks?
 *
 * The gate for the signature button, and asserted again server-side before an
 * acceptance is written — a customer page can be edited, a database cannot.
 * `required` is empty for every contract built today and non-empty for a frozen
 * one from before 2026-08-16.
 */
export function signatureIsComplete(
  signature: {
    initials: Readonly<Record<string, unknown>>;
    consented: boolean;
    signerName: string;
  },
  required: readonly string[] = CONTRACT_INITIAL_KEYS,
): boolean {
  if (!signature.consented) return false;
  if (signature.signerName.trim().length < 2) return false;
  return required.every((key) => Boolean(signature.initials[key]));
}

/**
 * Which checkpoints are still untouched, for the "you missed one" nudge.
 *
 * `required` defaults to what the contract asks for today, and the customer's
 * page passes what the document in front of them actually asks for instead.
 * The two differ: a presented document is a frozen snapshot (ADR 0032), so one
 * sent before 2026-08-13 still draws six initial boxes while this list now
 * holds none. Validating a six-box document against none would let the Sign
 * button light up with six boxes on screen still empty, and record an
 * acceptance that does not match the paper it belongs to.
 */
export function missingInitials(
  initials: Readonly<Record<string, unknown>>,
  required: readonly string[] = CONTRACT_INITIAL_KEYS,
): string[] {
  return required.filter((key) => !initials[key]);
}

/**
 * Which trade a repair is written against, given the trades on file.
 *
 * A custom repair has to land on a repair trade or nothing else works: the
 * repair contract is chosen by trade (`contractKindFor`), and the automatic
 * permit and manufacturer warranty are charged only on a replacement, the
 * permit on new construction too (`computeAutoExpenses`). A repair estimate
 * left on "Roofing Replacement"
 * quietly prices a permit and a Silver Pledge into a $600 patch.
 *
 * Trades are free text and are never renamed (rule 8), so this reads the names
 * rather than naming one: a roof repair goes on the roofing repair trade if
 * there is one, otherwise on whichever repair trade exists. A database with no
 * repair trade at all returns null, and the estimate falls back to the job's
 * bid type — which is the same answer `contractKindFor` already gives an
 * estimate carrying no trade.
 */
export function defaultRepairTrade(trades: readonly string[]): string | null {
  const repairs = trades.filter(isRepairTrade);
  return (
    repairs.find((t) => t.toLowerCase().includes("roof")) ?? repairs[0] ?? null
  );
}

/** Whether a trade names repair work. The other side of `isReplacementTrade`. */
export function isRepairTrade(trade: string | null | undefined): boolean {
  const name = (trade ?? "").trim().toLowerCase();
  return name !== "" && name.includes("repair");
}
